import { resultCodebookSchema } from './response-codebook.js';
import { resultQuestionNavigationSchema, resultRespondentGridSchema } from './shopify-study-results-view.js';
import { studyAnalysisOutcomeSchema } from './study-analysis.js';
import { responsePhraseResultsSchema } from './response-phrases.js';
import { shopifyStudyInquiryResultsSchema } from './shopify-study-inquiry-results.js';
import { shopifyStudyDecisionClassificationResultsSchema } from './shopify-study-decision-classifications.js';
export { shopifyStudyDecisionClassificationResultsSchema } from './shopify-study-decision-classifications.js';
/**
 * What: Zod schema and inferred types for the versioned `shopify-study-results.v1`
 * TestFeed-to-Shopify study results transport contract.
 * Transport / data-flow: Pure shared schema consumed by the Web app (response
 * assembler), the Shopify Worker client (response receiver), and the Shopify UI
 * (typed read model). No Worker, D1, R2, HTTP, scoring, or response-theme
 * derivation logic lives here — only the wire shape and runtime validation helpers.
 *
 * Algorithm or layout: The schema mirrors the literal serialized payload produced by
 * `apps/web/src/routes/integrations-shopify-internal.ts` and consumed by
 * `apps/shopify/src/testfeed-setup-packet.ts` and `apps/shopify/app/typesResults.ts`.
 * Persisted JSON is not validated by TypeScript assertions. The current observation reader validates
 * accepted report/decision identity, hashes and its consumed report fields; the direct results
 * response validates assembled analytics and its required complete `view` before transport.
 * Nullable analytics fields remain explicit absence. Current React results consume only the typed
 * view; raw/provenance fields remain available for scoped operator inspection.
 */
import * as z from 'zod';
import { shopifySetupPacketStatusLatestRunSchema } from './shopify-setup-packet-list.js';
import { shopifyTreatmentIdentityModeSchema } from './shopify-treatment-identity.js';
import { recommendationChangeListSchema } from './recommendation-change-list.js';
import { productFieldPreviewContextSchema } from './product-field-binding.js';
/**
 * The literal schema version stamp carried by every `shopify-study-results.v1`
 * response. The Web route sets this on every response; the Shopify client rejects
 * any payload whose `schemaVersion` does not equal this string.
 */
export const SHOPIFY_STUDY_RESULTS_SCHEMA_VERSION = 'shopify-study-results.v1';
/**
 * The single product field a Shopify study compares across its arms. This flow is
 * single-dimension, so the whole study carries one treatment field. Results cards read
 * this explicit signal instead of inferring the treatment kind from display strings: a
 * description study renders and validates run-owned descriptions, every other kind ignores
 * a stray `description` on a row.
 */
export const shopifyResultsTreatmentFieldSchema = z.enum(['claim', 'promotion', 'price', 'image', 'description']);
const ratingBandSchema = z.enum(['high', 'middle', 'low', 'unknown']);
const intentSkewSchema = z.enum(['high', 'middle', 'low', 'mixed', 'unknown']);
export const shopifyRelatedResultCompatibilitySchema = z.enum([
    'same_method_related_setup',
    'changed_setup',
    'changed_audience',
    'different_method',
    'unknown',
]);
export const shopifyComparisonDirectionSchema = z.enum(['up', 'down', 'flat']);
/**
 * Decision summary shared by the selected run summary and related-result summary.
 * Mirrors `RunObservation['decision']` from `apps/web/src/lib/study-run-observation.ts`,
 * which is itself the public projection of `MultiArmDecisionArtifact['decision']` plus
 * `winner_arm_id`, `runner_up_arm_id`, `margin_pp`, and `recommended_action`.
 *
 * Unknown nonempty decision names survive the full writer-shape/study/hash checks so the server
 * view can return unavailable/unknown_decision. They cannot become a default winner. IDs and
 * finite saved margins come from the accepted artifact, never the browser. Optional action is
 * retained on this raw diagnostic projection; the complete view supplies merchant advice.
 */
const decisionSummarySchema = z.object({
    decision: z.string(),
    winner_arm_id: z.string().nullable(),
    runner_up_arm_id: z.string().nullable(),
    margin_pp: z.number().nullable(),
    recommended_action: z.string().nullable().optional(),
});
/**
 * Reserved excerpt shape in the deployed results wire contract. Current results
 * use decisionClassifications and response phrases; there is no theme producer.
 */
const responseThemeExcerptSchema = z.object({
    text: z.string(),
    armId: z.string(),
    armLabel: z.string(),
    rating: z.number().nullable(),
    respondentLabel: z.string(),
});
const responseThemeSchema = z.object({
    id: z.string(),
    label: z.string(),
    count: z.number(),
    share: z.number(),
    intentSkew: intentSkewSchema,
    description: z.string(),
    representativeExcerpts: z.array(responseThemeExcerptSchema),
});
/**
 * Reserved optional results wire shape, retained without a reader or execution
 * path. Removing it is a separate shared-contract revision, not needed to retire
 * the obsolete coder. It does not enable theme generation or read old R2 artifacts.
 */
export const shopifyStudyResponseThemesSchema = z.object({
    summary: z.string(),
    themes: z.array(responseThemeSchema),
    uncategorizedCount: z.number(),
    status: z.enum(['complete', 'unavailable']).optional(),
    codebookVersion: z.string().optional(),
});
/**
 * A single open-ended response item returned in the selected-run `details.responses`
 * slice. Optional theme fields are reserved wire members, not populated by the
 * current classifier. Active per-answer labels use decisionClassifications.
 */
export const shopifyStudyResponseItemSchema = z.object({
    armId: z.string(),
    armLabel: z.string(),
    respondentLabel: z.string(),
    questionId: z.string(),
    response: z.string(),
    rating: z.number().nullable(),
    t2bContribution: z.number().nullable(),
    ratingBand: ratingBandSchema,
    themeId: z.string().nullable().optional(),
    themeIds: z.array(z.string()).nullable().optional(),
    decisionClassifications: z
        .array(z.object({
        factorId: z.string(),
        factorLabel: z.string(),
        role: z.enum(['driver', 'barrier', 'condition', 'delay', 'mention_only', 'unclear', 'role_unassigned']),
        probability: z.number().min(0).max(1).optional(),
        confidence: z.number().min(0).max(1).optional(),
    }))
        .optional(),
});
const responsesSliceSchema = z.object({
    total: z.number(),
    returned: z.number(),
    limit: z.number(),
    offset: z.number(),
    items: z.array(shopifyStudyResponseItemSchema),
});
export const shopifyStudyResultQuoteSchema = z.object({
    rating: z.number(),
    text: z.string(),
});
const armSegmentSchema = z.object({
    dimension: z.string(),
    segment: z.string(),
    n: z.number().nullable().optional(),
    t2b: z.number().nullable().optional(),
    t2bPercent: z.string().nullable().optional(),
    ratingDistribution: z.array(z.number()).nullable().optional(),
});
const armConfidenceSchema = z.object({
    t2bCiLow: z.number().nullable().optional(),
    t2bCiHigh: z.number().nullable().optional(),
    consensus: z.string().nullable().optional(),
    meanSemanticConfidence: z.number().nullable().optional(),
    ratingSd: z.number().nullable().optional(),
});
const armDistributionSchema = z.object({
    surveyPmf: z.array(z.number()).nullable().optional(),
    ratingDistribution: z.array(z.number()).nullable().optional(),
});
/**
 * Merchant-facing arm detail. Mirrors `ShopifyStudyResultArmDetail` from the prior
 * `apps/shopify/app/typesResults.ts` interface, with sub-fields of `confidence`,
 * `distribution`, and `segments` relaxed to `.nullable().optional()` to match the
 * runtime behavior of `buildShopifySelectedRunDetails` in
 * `apps/web/src/lib/integrations/shopify-results-helper.ts`. The assembler reads
 * persisted R2 report artifacts via `storage.readJson<StudyReport>(key)`, which
 * returns `StudyReport` at the type level but is `unknown` at runtime; the
 * assembler's truthy guards (`report?.confidence ?`, `report ?`, `report?.segments ?`)
 * check object existence but not sub-field existence, so legacy report artifacts
 * that predate a sub-field (e.g. `survey_pmf`) produce `undefined` at runtime. The
 * schema must accept what the assembler already tolerates; rejecting it would make
 * the route return 500 for runs that previously served results.
 */
export const shopifyStudyResultArmDetailSchema = z.object({
    respondentGrid: resultRespondentGridSchema,
    armId: z.string(),
    label: z.string(),
    imageUrl: z.string().nullable().optional(),
    treatmentDescription: z.string().nullable().optional(),
    description: z.string().nullable().optional(),
    claim: z.string().nullable().optional(),
    offer: z.string().nullable().optional(),
    imageCaption: z.string().nullable().optional(),
    price: z.string().nullable().optional(),
    isBaseline: z.boolean(),
    t2b: z.number().nullable(),
    t2bPercent: z.string().nullable(),
    verdict: z.string().nullable(),
    verdictBand: z.string().nullable(),
    presentation: z
        .object({
        label: z.string(),
        tone: z.enum(['positive', 'caution', 'neutral']),
        icon: z.enum(['check', 'next', 'none']),
        isRecommended: z.boolean(),
    })
        .nullable()
        .optional(),
    confidence: armConfidenceSchema.nullable(),
    distribution: armDistributionSchema.nullable(),
    segments: z.array(armSegmentSchema),
    topQuotes: z.array(shopifyStudyResultQuoteSchema),
});
/**
 * Selected-run details. `arms` is always present when `details` is present; the
 * `responses` and `responseThemes` fields are optional because the merchant-facing
 * payload (`buildMerchantSelectedRunDetails`) omits `responses` while keeping
 * `responseThemes`.
 */
/**
 * One original standard, merchant-authored or attachment question and its saved answers.
 *
 * Deliberately carries no share, percentage, or ranking. Score membership comes from the frozen
 * question definition; displaying an answer does not change membership or recompute its score.
 */
export const shopifyStudyDiagnosticQuestionSchema = z.object({
    questionId: z.string(),
    label: z.string(),
    purpose: z.enum(['standard', 'merchant_authored', 'attachment_verification']).optional(),
    scoreBearing: z.boolean().optional(),
    responseCount: z.number(),
    answers: z.array(z.object({
        armId: z.string(),
        armLabel: z.string(),
        respondentLabel: z.string(),
        respondentId: z.string().optional(),
        answerId: z.string().optional(),
        response: z.string(),
        traceId: z.string().optional(),
        assessment: z.enum(['inconclusive', 'unavailable']).optional(),
        assessmentReason: z.string().optional(),
        attachment: z
            .object({
            id: z.string(),
            label: z.string(),
            sourceUrl: z.string().nullable().optional(),
            storageKey: z.string().nullable().optional(),
            hash: z.string().nullable().optional(),
            mimeType: z.string().nullable().optional(),
            byteLength: z.number().nullable().optional(),
            order: z.number().nullable().optional(),
            status: z.string().nullable().optional(),
        })
            .nullable()
            .optional(),
    })),
});
export const shopifyStudyDiagnosticResponsesSchema = z.object({
    navigation: resultQuestionNavigationSchema.optional(),
    questions: z.array(shopifyStudyDiagnosticQuestionSchema),
    total: z.number(),
    returned: z.number(),
});
/**
 * Selected-run details. `arms` is always present when `details` is present; the
 * `responses` and `responseThemes` fields are optional because the merchant-facing
 * payload (`buildMerchantSelectedRunDetails`) omits `responses` while keeping
 * `responseThemes`. `diagnosticResponses` is optional because most runs have no
 * merchant-authored questions.
 */
const selectedRunDetailsSchema = z.object({
    analysisSections: z
        .array(z.object({
        kind: z.enum(['response_phrases', 'decision_classifications']),
        disclosure: z.boolean(),
    }))
        .optional(),
    analysisOutcomes: z.array(studyAnalysisOutcomeSchema).optional(),
    evidenceCoverage: z.enum(['complete', 'incomplete', 'not_requested']).optional(),
    resultTabs: z.array(z.enum(['overview', 'segments', 'responses', 'your_questions'])).optional(),
    arms: z.array(shopifyStudyResultArmDetailSchema),
    responses: responsesSliceSchema.optional(),
    responseThemes: shopifyStudyResponseThemesSchema.optional(),
    decisionClassifications: shopifyStudyDecisionClassificationResultsSchema.optional(),
    responsePhrases: responsePhraseResultsSchema.optional(),
    responseCodebook: resultCodebookSchema.optional(),
    diagnosticResponses: shopifyStudyDiagnosticResponsesSchema.optional(),
});
const selectedRunSummaryArmSchema = z.object({
    arm_id: z.string(),
    label: z.string(),
    is_baseline: z.boolean(),
    t2b: z.number().nullable(),
    t2b_percent: z.string().nullable(),
    ci_low: z.number().nullable(),
    ci_high: z.number().nullable(),
    verdict: z.string().nullable(),
    verdict_band: z.string().nullable(),
});
const selectedRunSummarySchema = z.object({
    run_id: z.string(),
    status: z.string(),
    requested_at: z.string(),
    finished_at: z.string().nullable(),
    model: z.string().nullable(),
    n: z.number().nullable(),
    population_id: z.string().nullable(),
    calibration_status: z.string().nullable(),
    arms: z.array(selectedRunSummaryArmSchema),
    decision: decisionSummarySchema.nullable(),
});
/**
 * A historical run row in the `runs` array. `latestStepKey`, `finishedAt`, and
 * `model` are optional-or-nullable exactly as in the prior interface: `latestStepKey`
 * and `model` are `optional` (may be omitted), `finishedAt` is `nullable` (always
 * present, possibly `null`).
 */
export const shopifyStudyResultsRunSchema = z.object({
    id: z.string(),
    status: z.string(),
    latestStepKey: z.string().nullable().optional(),
    reportReady: z.boolean(),
    requestedAt: z.string(),
    finishedAt: z.string().nullable().optional(),
    model: z.string().nullable().optional(),
});
export const shopifySelectedStudyRunSchema = z.object({
    run: shopifyStudyResultsRunSchema,
    summary: selectedRunSummarySchema,
    /** Canonical progress for this selected run, never the latest run from another visit. */
    progress: shopifySetupPacketStatusLatestRunSchema.nullable().optional(),
    // Historical result payloads predate explicit identity mode; normalize them as bound.
    treatmentMode: shopifyTreatmentIdentityModeSchema.default('bound'),
    // The single product field this study compares, resolved by the web projection from the
    // run's arm treatment keys. Nullable/optional because historical payloads predate it and a
    // run with no resolvable treatment field carries `null`. Cards read this rather than
    // inferring the treatment kind from display strings.
    treatmentField: shopifyResultsTreatmentFieldSchema.nullable().optional(),
    details: selectedRunDetailsSchema.optional(),
    inquiry: shopifyStudyInquiryResultsSchema.optional(),
    changeList: recommendationChangeListSchema.nullable().optional(),
    // Display-only product context resolved for the selected run independently of the change
    // list, so running studies and packets without a decision artifact still carry a price and
    // image for the treatment cards. The client reads `previewContext ?? changeList.previewContext`.
    previewContext: productFieldPreviewContextSchema.nullable().optional(),
});
const relatedResultSummaryArmSchema = z.object({
    armId: z.string(),
    label: z.string(),
    isBaseline: z.boolean(),
    t2bPercent: z.string().nullable(),
    verdictBand: z.string().nullable(),
});
const relatedResultComparisonClaimSchema = z.object({
    armId: z.string(),
    label: z.string(),
    selectedT2bPercent: z.string().nullable(),
    relatedT2bPercent: z.string().nullable(),
    deltaPp: z.number(),
    direction: shopifyComparisonDirectionSchema,
});
const relatedResultComparisonAudienceSchema = z.object({
    selectedPopulationId: z.string().nullable(),
    relatedPopulationId: z.string().nullable(),
    selectedN: z.number().nullable(),
    relatedN: z.number().nullable(),
    /** Survey-artifact `n_responses` / `n_personas` of the arm with the biggest shortfall; absent on older payloads. */
    selectedAnswered: z.number().nullable().optional(),
    selectedPlanned: z.number().nullable().optional(),
    relatedAnswered: z.number().nullable().optional(),
    relatedPlanned: z.number().nullable().optional(),
    changed: z.boolean(),
});
/**
 * A related-result summary surfaced alongside the selected run. Mirrors
 * `ShopifyRelatedStudyResult` from the prior `apps/shopify/app/typesResults.ts`
 * interface and the parallel `ShopifyRelatedStudyResult` type in
 * `apps/web/src/lib/integrations/shopify-related-study-results.ts`. The two
 * implementations are now unified through this schema.
 */
export const shopifyRelatedStudyResultSchema = z.object({
    setupPacketId: z.string(),
    studyId: z.string(),
    runId: z.string(),
    completedAt: z.string().nullable(),
    product: z.object({
        id: z.string(),
        title: z.string(),
    }),
    compatibility: shopifyRelatedResultCompatibilitySchema,
    comparisonSafe: z.boolean(),
    summary: z.object({
        model: z.string().nullable(),
        n: z.number().nullable(),
        populationId: z.string().nullable(),
        // Historical related-result payloads predate explicit identity mode; normalize as bound.
        treatmentMode: shopifyTreatmentIdentityModeSchema.default('bound'),
        decision: decisionSummarySchema.nullable(),
        arms: z.array(relatedResultSummaryArmSchema),
    }),
    comparison: z.object({
        claims: z.array(relatedResultComparisonClaimSchema),
        audience: relatedResultComparisonAudienceSchema,
    }),
});
/** A single Arms tab table row, mirroring `ShopifyArmsRow` from `shopify-arms-rows.ts`. */
export const shopifyArmsRowSchema = z.object({
    armId: z.string(),
    label: z.string(),
    displayLabel: z.string(),
    imageUrl: z.string().nullable().optional(),
    treatmentDescription: z.string().nullable().optional(),
    description: z.string().nullable().optional(),
    claim: z.string().nullable().optional(),
    offer: z.string().nullable().optional(),
    imageCaption: z.string().nullable().optional(),
    price: z.string().nullable().optional(),
    isBaseline: z.boolean(),
    t2bPercent: z.string().nullable(),
    confidenceLabel: z.string().nullable(),
    verdictBand: z.string().nullable(),
    presentation: z
        .object({
        label: z.string(),
        tone: z.enum(['positive', 'caution', 'neutral']),
        icon: z.enum(['check', 'next', 'none']),
        isRecommended: z.boolean(),
    })
        .nullable()
        .optional(),
});
/**
 * A results section names one client component and carries its finished props.
 * Deliberately does not reuse `sduiSectionSchema` from `@testfeed/integration-contracts`
 * — that schema models a section as a list of generic widgets, which is the abstraction
 * that caused the results page to be silently redesigned (#1965). A results section
 * instead names one client component (`id`) and carries its finished props verbatim:
 * structured values and IDs only, never merchant-visible copy. When a second section
 * type lands, this becomes a `z.discriminatedUnion('id', [...])`.
 */
export const shopifyResultsSectionSchema = z.object({
    id: z.literal('arms_table'),
    props: z.object({
        rows: z.array(shopifyArmsRowSchema),
        productTitle: z.string().nullable().optional(),
        productDescription: z.string().nullable().optional(),
        displayPrice: z.string().nullable().optional(),
        decision: z.string().nullable().optional(),
        winnerArmId: z.string().nullable().optional(),
        // Explicit study-level treatment field so the SDUI card path acts on the same signal as the
        // client-derived path rather than inferring the treatment kind from display strings.
        treatmentField: shopifyResultsTreatmentFieldSchema.nullable().optional(),
    }),
});
/**
 * A results tab layout. There is deliberately no `label` field: the tab list is
 * client-derived (resolved through `@testfeed/product-language`) and the server only
 * supplies a layout that attaches by ID.
 */
export const shopifyStudyResultsTabSchema = z.object({
    id: z.string(),
    sections: z.array(shopifyResultsSectionSchema),
});
export const shopifyStudyResultsTabsSchema = z.array(shopifyStudyResultsTabSchema);
/**
 * The complete `shopify-study-results.v1` wire payload. `study`, `selectedRun`, and
 * `relatedResults` are nullable-or-optional exactly as in the prior interface:
 * `study` and `selectedRun` are nullable (always present, possibly `null`),
 * `relatedResults` is optional (may be omitted entirely). `tabs` is optional and
 * present only when the `results-arms-sdui` flag is on and the assembled layout
 * validates; its absence is the fail-open degrade path back to the legacy client render.
 */
export const shopifyStudyResultsResponseSchema = z.object({
    schemaVersion: z.literal(SHOPIFY_STUDY_RESULTS_SCHEMA_VERSION),
    packet: z.object({
        packetId: z.string(),
        status: z.string(),
        productId: z.string().optional(),
        productTitle: z.string().nullable().optional(),
    }),
    study: z
        .object({
        studyId: z.string(),
        url: z.string(),
    })
        .nullable(),
    runs: z.array(shopifyStudyResultsRunSchema),
    selectedRunId: z.string().nullable(),
    selectedRun: shopifySelectedStudyRunSchema.nullable(),
    relatedResults: z.array(shopifyRelatedStudyResultSchema).optional(),
    tabs: shopifyStudyResultsTabsSchema.optional(),
});
/**
 * Parses and validates an unknown value as a `shopify-study-results.v1` response.
 * Returns the typed payload on success or `null` on failure — callers (Web route
 * assembler self-check, Shopify client receive path) decide how to surface the
 * failure. Does not throw; the engine never falls back to an assumed shape.
 *
 * Use `safeParseShopifyStudyResultsResponse` when you need the structured Zod error
 * for diagnostics (e.g. the Web route's fail-loud path).
 */
export function parseShopifyStudyResultsResponse(input) {
    const result = shopifyStudyResultsResponseSchema.safeParse(input);
    return result.success ? result.data : null;
}
/**
 * Strict parse that returns either the typed payload or the Zod error. Used by the
 * Web route to fail loud with a 500 when an internally-assembled response does not
 * match the contract, and by the Shopify client to reject malformed upstream JSON
 * before exposing it to callers.
 */
export function safeParseShopifyStudyResultsResponse(input) {
    return shopifyStudyResultsResponseSchema.safeParse(input);
}
//# sourceMappingURL=shopify-study-results-v1.js.map